import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { PublicShareLayout } from "@/components/document-sharing/PublicShareLayout";

interface ShareMeta {
  id: string;
  mode: "file" | "folder_snapshot" | "folder_live_mirror";
  expiresAt: string;
  emailGateEnabled: boolean;
  requiresPin: boolean;
  message: string | null;
  createdAt: string;
  sharedBy: string | null;
  expired: boolean;
}

interface FolderItem {
  fileId: string;
  fileName: string;
  mimeType: string | null;
  fileSizeBytes: number | null;
  versionPinned: number;
  addedAt: string;
}

export function PublicFolderShareViewer({ token }: { token: string }) {
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const metaQuery = useQuery({
    queryKey: ["public-share-folder", token, "meta"],
    queryFn: async () => {
      const res = await fetch(`/api/share/${encodeURIComponent(token)}/meta`);
      if (!res.ok) throw new Error("Share not found");
      const body = (await res.json()) as { data: ShareMeta };
      return body.data;
    },
    retry: false,
  });

  const contentsQuery = useQuery({
    queryKey: ["public-share-folder", token, "contents"],
    queryFn: async () => {
      const res = await fetch(`/api/share/${encodeURIComponent(token)}/folder/contents`);
      if (!res.ok) throw new Error("Forbidden");
      const body = (await res.json()) as { data: FolderItem[] };
      return body.data;
    },
    enabled:
      unlocked ||
      (metaQuery.data ? !metaQuery.data.emailGateEnabled && !metaQuery.data.requiresPin : false),
    retry: false,
  });

  if (metaQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Loading…</p>
      </div>
    );
  }

  const meta = metaQuery.data;
  if (metaQuery.isError || !meta) {
    return (
      <PublicShareLayout sharedBy={null}>
        <h1 className="text-2xl font-semibold">Link unavailable</h1>
        <p className="mt-2 text-gray-600">This share link has been disabled or not found.</p>
      </PublicShareLayout>
    );
  }
  if (meta.expired) {
    return (
      <PublicShareLayout sharedBy={meta.sharedBy} maxContentWidth="wide">
        <h1 className="text-2xl font-semibold">Link has expired</h1>
      </PublicShareLayout>
    );
  }

  const needsUnlock = meta.emailGateEnabled || meta.requiresPin;
  if (needsUnlock && !unlocked) {
    return (
      <PublicShareLayout sharedBy={meta.sharedBy} maxContentWidth="wide">
        {meta.message && (
          <div className="mb-6 rounded border border-gray-200 bg-white p-4 text-sm text-gray-700">
            {meta.message}
          </div>
        )}
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setSubmitting(true);
            setUnlockError(null);
            try {
              const res = await fetch(`/api/share/${encodeURIComponent(token)}/unlock`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  email: meta.emailGateEnabled ? email : undefined,
                  pin: meta.requiresPin ? pin : undefined,
                }),
              });
              if (!res.ok) {
                const body = (await res.json().catch(() => ({}))) as { error?: string };
                setUnlockError(body.error ?? "Could not unlock");
                return;
              }
              setUnlocked(true);
            } finally {
              setSubmitting(false);
            }
          }}
          className="space-y-4"
        >
          {meta.emailGateEnabled && (
            <input
              type="email"
              required
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="block w-full rounded border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          )}
          {meta.requiresPin && (
            <input
              type="password"
              required
              placeholder="Access PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              className="block w-full rounded border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          )}
          {unlockError && <p className="text-sm text-red-600">{unlockError}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? "Unlocking…" : "View folder"}
          </button>
        </form>
      </PublicShareLayout>
    );
  }

  return (
    <PublicShareLayout sharedBy={meta.sharedBy} maxContentWidth="wide">
      {meta.message && (
        <div className="mb-4 rounded border border-gray-200 bg-white p-4 text-sm text-gray-700">
          {meta.message}
        </div>
      )}
      <div className="mb-4 flex items-center justify-between">
        <span className="text-xs text-gray-500">
          Expires {new Date(meta.expiresAt).toLocaleDateString()}
        </span>
        <a
          href={`/api/share/${encodeURIComponent(token)}/folder/download.zip`}
          className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Download all (.zip)
        </a>
      </div>
      <div className="overflow-hidden rounded border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                File
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                Size
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                Version
              </th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {contentsQuery.data?.map((f) => (
              <tr key={f.fileId}>
                <td className="px-4 py-2 text-sm text-gray-900">{f.fileName}</td>
                <td className="px-4 py-2 text-sm text-gray-500">{formatSize(f.fileSizeBytes)}</td>
                <td className="px-4 py-2 text-sm text-gray-500">v{f.versionPinned}</td>
                <td className="px-4 py-2 text-right">
                  <a
                    className="text-sm text-blue-600 hover:underline"
                    href={`/api/share/${encodeURIComponent(token)}/folder/files/${f.fileId}?download=1`}
                  >
                    Download
                  </a>
                </td>
              </tr>
            )) ?? null}
          </tbody>
        </table>
      </div>
    </PublicShareLayout>
  );
}

function formatSize(bytes: number | null): string {
  if (!bytes) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
